import { BASE_HERO_SLOTS, recordHeroBest, unlockedHeroes, normalizeHeroSave, type HeroBest, type HeroId, type HeroSave } from '@knight/shared';
import * as S from './state';

// ------------------------------------------------------------------ hero tree progress

export function loadLocalHeroBest(): HeroBest {
  try {
    const raw = localStorage.getItem('knight.heroBest');
    if (raw) {
      const b = JSON.parse(raw) as HeroBest;
      if (b && typeof b === 'object') return b;
    }
  } catch {
    /* ignore */
  }
  return {};
}

/** Remembers the best rank reached with a hero (the hero tree unlocks on ranks). */
export function recordLocalHeroBest(hero: HeroId, rank: number): void {
  const best = loadLocalHeroBest();
  if (!recordHeroBest(best, hero, rank)) return;
  try {
    localStorage.setItem('knight.heroBest', JSON.stringify(best));
  } catch {
    /* ignore */
  }
  S.heroesTick.value++;
}

/** Best levels per hero for the current connection mode. */
export function heroBestView(): HeroBest {
  const acc = S.account.value;
  void S.heroesTick.value;
  if (S.conn.value === 'online' && acc) return acc.heroBest ?? {};
  return loadLocalHeroBest();
}

export function unlockedView(): HeroId[] {
  return unlockedHeroes(heroBestView());
}

/** Offline heroes live in the browser; online they come from the account. */
export function loadLocalHeroes(): HeroSave[] {
  try {
    const raw = localStorage.getItem('knight.heroes');
    if (raw) {
      const list = JSON.parse(raw) as unknown[];
      if (Array.isArray(list)) return list.map((h) => normalizeHeroSave(h)).filter((h): h is HeroSave => !!h);
    }
  } catch {
    /* ignore */
  }
  return [];
}

function saveLocalHeroes(list: HeroSave[]): void {
  try {
    localStorage.setItem('knight.heroes', JSON.stringify(list));
  } catch {
    /* ignore */
  }
  S.heroesTick.value++;
}

export function localHero(slot: number): HeroSave | null {
  return loadLocalHeroes().find((h) => h.slot === slot) ?? null;
}

export function saveLocalHero(save: HeroSave): void {
  const list = loadLocalHeroes().filter((h) => h.slot !== save.slot);
  list.push(save);
  list.sort((a, b) => a.slot - b.slot);
  saveLocalHeroes(list);
}

export function deleteLocalHero(slot: number): void {
  saveLocalHeroes(loadLocalHeroes().filter((h) => h.slot !== slot));
}

/**
 * Where a NEW hero may go: the chosen slot when it is empty, otherwise the first free one.
 * Returns -1 when every slot holds a living hero, because a hero is never overwritten.
 */
export function freeHeroSlot(preferred = S.slotChoice.value): number {
  const hv = heroesView();
  const taken = new Set(hv.heroes.map((h) => h.slot));
  if (preferred >= 0 && preferred < hv.slots && !taken.has(preferred)) return preferred;
  for (let i = 0; i < hv.slots; i++) if (!taken.has(i)) return i;
  return -1;
}

/** Heroes and slot count for the current connection mode; subscribes to the relevant signals. */
export function heroesView(): { heroes: HeroSave[]; slots: number } {
  const acc = S.account.value;
  const tick = S.heroesTick.value;
  void tick;
  if (S.conn.value === 'online' && acc) return { heroes: acc.heroes ?? [], slots: acc.heroSlots ?? BASE_HERO_SLOTS };
  return { heroes: loadLocalHeroes(), slots: BASE_HERO_SLOTS };
}
